import { VStack, Box, Flex, Text, IconButton } from '@chakra-ui/react';
import { EditIcon, DeleteIcon, WarningIcon } from '@chakra-ui/icons';

import type { AutoClaim, PropertyClaim } from 'api/quote-marketplace';

type Claim = AutoClaim | PropertyClaim;

interface ClaimsListProps<T extends Claim> {
  claims: T[];
  formatDisplay: (claim: T) => string;
  onEdit: (index: number, claim: T) => void;
  onDelete: (index: number) => void;
  isIncomplete: (claim: T) => boolean;
  isUpdating?: boolean;
}

const ClaimsList = <T extends Claim>({
  claims,
  formatDisplay,
  onEdit,
  onDelete,
  isIncomplete,
  isUpdating = false,
}: ClaimsListProps<T>) => {
  return (
    <VStack spacing={1} align="stretch" pl={2}>
      {claims.map((claim, idx) => {
        const incomplete = isIncomplete(claim);
        return (
          <Box
            key={claim.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {incomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={incomplete ? 'orange.700' : 'gray.700'}
              >
                • {formatDisplay(claim)}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => onEdit(idx, claim)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => onDelete(idx)}
                isDisabled={isUpdating}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );
};

export default ClaimsList;
